import Controller from "controllers/application_controller"

/**
 * Drives a Ui::Confirm dialog and resolves with the user's answer: `true`
 * from a plain confirm, the checked radio's *value* when the dialog offers
 * choices (e.g. "Write off" vs "Invoice later"), and `null` when
 * cancelled/escaped. When the dialog contains radios, the proceed button
 * stays disabled until one is selected.
 *
 * Every dialog registers by id in a screen-level registry, so it can be
 * triggered from anywhere — including legacy (Prototype) code:
 *
 *   const choice = await window.confirmActions["my_dialog_id"].confirm("Body text")
 *
 * Turbo's form confirmation (data-turbo-confirm) is wired up separately in
 * lib/turbo-extensions, which looks up the dialog with id "turbo_confirm".
 */
export default class ConfirmController extends Controller {
  static identifier = "confirm"
  static targets = ["message", "proceed"]

  // Promise resolver for the pending confirmation, if any
  resolve = null

  connect() {
    // A Turbo-cached snapshot can restore the dialog with [open] set but no
    // pending promise, leaving Cancel unable to close it — start closed.
    if (this.element.open) {
      this.element.close()
    }

    // The dialog's close event also fires for the Escape key
    this.addEventListener("close", () => this.cancel())

    // Events inside the dialog shouldn't propagate to the application behind it
    this.addEventListener("click", event => event.stopPropagation())
    this.addEventListener("mousedown", event => event.stopPropagation())

    if (this.element.id) {
      window.confirmActions ??= {}
      window.confirmActions[this.element.id] = this
    }
  }

  disconnect() {
    this.settle(null)

    if (this.element.id && window.confirmActions?.[this.element.id] === this) {
      delete window.confirmActions[this.element.id]
    }

    super.disconnect()
  }

  // Show the dialog and return a promise of the user's answer
  confirm(message) {
    if (this.resolve) {
      this.settle(null)
    }

    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message || "Are you sure?"
    }

    this.reset()

    return new Promise(resolve => {
      this.resolve = resolve
      this.element.showModal()
    })
  }

  // Enable the proceed button once an option is picked
  select() {
    if (this.hasProceedTarget) {
      this.proceedTarget.disabled = false
    }
  }

  proceed() {
    if (this.radios.length === 0) {
      this.settle(true)
    } else {
      const chosen = this.element.querySelector("input[type=radio]:checked")

      if (chosen) {
        this.settle(chosen.value)
      }
    }
  }

  cancel() {
    this.settle(null)
  }

  // Clear any prior selection so a reused dialog opens blank, with proceed
  // disabled until a choice is made (or enabled when there's no choice)
  reset() {
    this.radios.forEach(radio => {
      radio.checked = false
    })

    if (this.hasProceedTarget) {
      this.proceedTarget.disabled = this.radios.length > 0
    }
  }

  get radios() {
    return this.element.querySelectorAll("input[type=radio]")
  }

  settle(value) {
    const resolve = this.resolve
    if (!resolve) return

    this.resolve = null
    if (this.element.open) {
      this.element.close()
    }
    resolve(value)
  }
}
